/**
 * Evergreen (URL-driven) Gift With Purchase.
 *
 * Parity port of the live theme's app/utilities/landingGwp.js: landing on any
 * URL with ?gwp=<variantId>&gwp_threshold=<cents> persists the offer in
 * sessionStorage for the visit. On every cart change the gift variant is added
 * (line property `_Evergreen Gwp`, already rendered as a gift by the cart
 * drawer/progress bar) once the subtotal excluding the gift meets the
 * threshold, and removed when it drops below or the session offer is gone.
 * The gift's price is zeroed by store-level discount logic keyed on the line
 * property, not by the theme.
 */
const VARIANT_PARAM = 'gwp';
const THRESHOLD_PARAM = 'gwp_threshold';
const GWP_PROPERTY = '_Evergreen Gwp';
const INITIATOR = 'cart-evergreen-gwp';

export class CartEvergreenGwp extends HTMLElement {
  constructor() {
    super();
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onCartOpen = this.onCartOpen.bind(this);
    this.processing = false;
  }

  connectedCallback() {
    this.captureUrlParams();
    // Engine-driven cart changes (drawer steppers, data-ajax-cart-* controls).
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    // PDP/quick-add ATC flows post /cart/add.js directly (cart-hydrate) and
    // announce themselves with a window `cart-open` — no engine event fires.
    window.addEventListener('cart-open', this.onCartOpen);
    // The live implementation also evaluates on page load (a returning visitor
    // can already meet the threshold without triggering a cart request).
    this.initialCheck();
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    window.removeEventListener('cart-open', this.onCartOpen);
  }

  onCartOpen() {
    this.fetchCartAndCheck();
  }

  captureUrlParams() {
    const isNumeric = (value) => /^-?\d+$/.test(value);
    const { searchParams } = new URL(window.location);
    const variant = searchParams.get(VARIANT_PARAM);
    const threshold = searchParams.get(THRESHOLD_PARAM);

    if (variant !== null && threshold !== null && isNumeric(variant) && isNumeric(threshold)) {
      sessionStorage.setItem(VARIANT_PARAM, variant);
      sessionStorage.setItem(THRESHOLD_PARAM, threshold);
    }
  }

  getOffer() {
    const variant = Number(sessionStorage.getItem(VARIANT_PARAM));
    const threshold = Number(sessionStorage.getItem(THRESHOLD_PARAM));
    if (!variant || Number.isNaN(threshold)) return null;
    return { variant, threshold };
  }

  onCartUpdate(event) {
    const { requestState } = event.detail || {};
    if (!requestState?.responseData) return;

    if (requestState.info?.initiator === INITIATOR) {
      // Our own add failed with "Cannot find variant" — the campaign link
      // points at a dead variant, so drop the offer instead of retrying on
      // every subsequent cart update.
      if (!requestState.responseData.ok && requestState.responseData.status === 422) {
        sessionStorage.removeItem(VARIANT_PARAM);
        sessionStorage.removeItem(THRESHOLD_PARAM);
      }
      return;
    }

    if (!requestState.responseData.ok) return;

    const cart = requestState.responseData.body;
    if (!cart || typeof cart.total_price === 'undefined') return;

    this.checkGwp(cart);
  }

  initialCheck() {
    this.fetchCartAndCheck();
  }

  fetchCartAndCheck() {
    if (!this.getOffer()) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => this.whenEngineReady(() => this.checkGwp(cart)))
      .catch(() => {});
  }

  // liquidAjaxCart is published by native-ajax-cart; the initial page-load
  // check can run before that module has executed.
  whenEngineReady(fn, attempt = 0) {
    if (window.liquidAjaxCart) return fn();
    if (attempt > 20) return;
    setTimeout(() => this.whenEngineReady(fn, attempt + 1), 250);
  }

  checkGwp(cart) {
    if (this.processing) return;

    const { items } = cart;
    if (!items) return;

    const offer = this.getOffer();
    const gwpItem = items.find((item) => item.properties?.[GWP_PROPERTY]);
    const hasGwp = !!gwpItem;

    if (!offer) {
      // Session offer gone (e.g. new session): clean a stray gift, as live does.
      if (hasGwp) this.removeItem(gwpItem);
      return;
    }

    const totalExGwp = items.reduce(
      (sum, item) => (item.properties?.[GWP_PROPERTY] ? sum : sum + item.final_line_price),
      0
    );
    const thresholdMet = totalExGwp >= offer.threshold;

    if (thresholdMet && !hasGwp && items.length) {
      this.processing = true;
      window.liquidAjaxCart.add(
        {
          items: [
            {
              id: offer.variant,
              quantity: 1,
              properties: { [GWP_PROPERTY]: true },
            },
          ],
        },
        {
          info: { initiator: INITIATOR },
          lastCallback: () => {
            this.processing = false;
          },
        }
      );
    } else if (!thresholdMet && hasGwp) {
      this.removeItem(gwpItem);
    }
  }

  removeItem(gwpItem) {
    this.processing = true;
    window.liquidAjaxCart.change(
      {
        id: gwpItem.key,
        quantity: 0,
      },
      {
        info: { initiator: INITIATOR },
        lastCallback: () => {
          this.processing = false;
        },
      }
    );
  }
}

if (!customElements.get('cart-evergreen-gwp')) {
  customElements.define('cart-evergreen-gwp', CartEvergreenGwp);
}
